import { Plus, Loader2, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { cn } from '@/utils/cn';
import { useShortcuts, useUpsertShortcut, type DentalShortcutRow } from '@/hooks/useDentalShortcuts';
import { useProcedureCodes, type DentalProcedureCodeRow } from '@/hooks/useProcedureCodes';
import { ShortcutButton } from './ShortcutButton';
import { ProcedureCodePicker } from './ProcedureCodePicker';
import { useState } from 'react';
import { Button } from '@/components/ui/Button';

interface ShortcutsPanelProps {
  clinicId: string;
  activeShortcutId: string | null;
  onShortcutToggle: (id: string) => void;
  className?: string;
}

export function ShortcutsPanel({
  clinicId,
  activeShortcutId,
  onShortcutToggle,
  className,
}: ShortcutsPanelProps) {
  const { t } = useTranslation();
  const { data: shortcuts, isLoading } = useShortcuts(clinicId);
  const { data: allCodes } = useProcedureCodes();
  const upsert = useUpsertShortcut(clinicId);
  const [showPicker, setShowPicker] = useState(false);

  const codeMap = new Map<string, DentalProcedureCodeRow>(allCodes?.map((c) => [c.code, c]) ?? []);

  const handleAddShortcut = async (row: DentalProcedureCodeRow) => {
    try {
      const nextOrder = (shortcuts?.length ?? 0) + 1;
      await upsert.mutateAsync({
        label: row.name_da.slice(0, 20),
        procedureCode: row.code,
        color: '#6366f1',
        displayOrder: nextOrder,
      });
      toast.success(t('dental.shortcuts.addShortcut'));
      setShowPicker(false);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Fejl ved tilføjelse af lynvalg';
      toast.error(msg);
    }
  };

  return (
    <div
      role="region"
      aria-label="Lynvalg"
      className={cn(
        'flex flex-col bg-ds-card border-r border-ds-line',
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center px-3 py-2.5 border-b border-ds-line flex-shrink-0">
        <div className="flex items-center gap-1.5">
          <Zap className="h-4 w-4 text-ds-teal shrink-0" />
          <span className="text-sm font-semibold text-ds-text">
            {t('dental.shortcuts.title')}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-2">
        {isLoading ? (
          <div className="grid grid-cols-3 gap-1.5">
            {Array.from({ length: 9 }).map((_, i) => (
              <div
                key={i}
                className="h-10 rounded-lg bg-ds-neutral-tint bg-ds-sunk animate-pulse"
              />
            ))}
          </div>
        ) : !shortcuts || shortcuts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-6 px-3 text-center">
            <p className="text-xs text-ds-muted">
              {t('dental.shortcuts.emptyState')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-1.5">
            {shortcuts.map((sc: DentalShortcutRow) => (
              <ShortcutButton
                key={sc.id}
                shortcut={sc}
                isActive={activeShortcutId === sc.id}
                onClick={(s) => onShortcutToggle(s.id)}
                tooltipName={codeMap.get(sc.procedure_code)?.name_da}
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-ds-line p-2">
        {activeShortcutId && (
          <Button
            type="button"
            variant="secondary"
            size="full"
            className="mb-1.5"
            onClick={() => onShortcutToggle(activeShortcutId)}
          >
            {t('dental.shortcuts.deactivateShortcut')}
          </Button>
        )}
        <Button
          type="button"
          variant="secondary"
          size="full"
          onClick={() => setShowPicker(true)}
          disabled={upsert.isPending}
        >
          {upsert.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
          {t('dental.shortcuts.addNew')}
        </Button>
      </div>

      {showPicker && (
        <ProcedureCodePicker
          asModal
          onSelect={handleAddShortcut}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}
